import type { DiagramDocument, DiagramEdge, DiagramNode, NodeStyle, NodeType } from './types';
import { getShapeDefinition, SHAPE_DEFINITIONS } from './shapeRegistry';

export const DEFAULT_NODE_STYLE: NodeStyle = {
  fill: '#ffffff',
  stroke: '#344054',
  strokeWidth: 1.5,
  textColor: '#101828',
  fontSize: 16,
  radius: 10,
  opacity: 1,
  linePattern: 'solid',
  textAlign: 'center',
  fontWeight: 550,
};

export const nodeSizeByType: Record<NodeType, { width: number; height: number }> = Object.fromEntries(
  SHAPE_DEFINITIONS.map((shape) => [shape.type, { width: shape.width, height: shape.height }]),
) as Record<NodeType, { width: number; height: number }>;

export function createNode(type: NodeType, x = 120, y = 120): DiagramNode {
  const definition = getShapeDefinition(type);
  const size = { width: definition.width, height: definition.height };
  const data: DiagramNode['data'] = {};
  if (type === 'bpmnTask') data.taskType = 'user';
  if (type === 'bpmnSubprocess') { data.subprocessType = 'embedded'; data.expanded = true; }
  if (type === 'bpmnDataObject') data.dataState = '';
  if (type === 'bpmnEvent') { data.eventType = 'start'; data.eventDefinition = 'none'; }
  if (type === 'bpmnBoundaryEvent') { data.eventType = 'boundary'; data.eventDefinition = 'timer'; data.attachedToId = null; data.cancelActivity = true; }
  if (type === 'bpmnTextAnnotation') data.annotationKind = 'text';
  if (type === 'bpmnGateway') data.gatewayType = 'exclusive';
  if (type === 'bpmnPool') data.participant = '';
  if (type === 'bpmnLane') data.responsible = '';
  if (type === 'orgPerson') { data.name = ''; data.role = ''; data.department = ''; }
  if (type === 'orgDepartment') data.department = '';
  if (type === 'mindRoot') data.topicKind = 'root';
  if (type === 'mindTopic') data.topicKind = 'topic';
  if (['networkServer', 'networkSwitch', 'networkRouter', 'networkFirewall', 'networkClient', 'networkCloud', 'networkLoadBalancer', 'networkDatabase', 'networkStorage'].includes(type)) {
    data.hostname = '';
    data.ip = '';
    data.vlan = '';
    data.status = 'unknown';
    data.location = '';
  }
  if (['cloudAws','cloudAzure','cloudGcp','cloudService'].includes(type)) {
    data.provider = type === 'cloudAws' ? 'AWS' : type === 'cloudAzure' ? 'Azure' : type === 'cloudGcp' ? 'Google Cloud' : '';
    data.service = ''; data.region = ''; data.environment = ''; data.account = '';
  }
  if (type === 'devCode') { data.language = 'TypeScript'; data.scope = 'frontend'; }
  if (type === 'devApi') { data.method = 'GET'; data.route = '/api/recurso'; }
  if (type === 'devMobile') { data.platform = 'Android / iOS'; }
  if (type === 'devBrowser') { data.stack = 'Web app'; }
  if (type === 'engineeringSensor') { data.unit = '°C'; data.value = ''; }
  if (type === 'engineeringController') { data.kind = 'CLP'; data.model = ''; }
  if (type === 'engineeringFormula') { data.formula = 'F = m × a'; }
  if (type === 'devTerminal') { data.shell = 'PowerShell'; data.command = ''; }
  if (type === 'devContainer') { data.image = ''; data.runtime = 'Docker'; }
  if (type === 'devQueue') { data.broker = ''; data.topic = ''; }
  if (type === 'devFunction') { data.signature = 'executar()'; data.language = 'TypeScript'; }
  if (type === 'orgExecutive') { data.name = ''; data.role = 'Diretoria'; data.department = ''; }
  if (type === 'orgTeam') { data.department = ''; data.members = ''; }
  if (type === 'networkVpn') { data.protocol = 'VPN'; data.endpoint = ''; }
  if (type === 'networkRack') { data.location = ''; data.units = '42U'; }
  if (type === 'engineeringMotor') { data.power = ''; data.rpm = ''; }
  if (type === 'engineeringBattery') { data.voltage = ''; data.capacity = ''; }
  if (type === 'physicsVector') { data.magnitude = ''; data.unit = ''; }
  if (type === 'physicsWave') { data.frequency = ''; data.amplitude = ''; }
  const entityFields = type === 'erdEntity' ? [{ id: crypto.randomUUID(), name: 'id', dataType: 'UUID', primaryKey: true, foreignKey: false, nullable: false }] : undefined;
  const umlAttributes = type === 'umlClass' ? ['+ id: UUID'] : undefined;
  const umlMethods = type === 'umlClass' ? ['+ salvar(): void'] : undefined;

  return {
    id: crypto.randomUUID(),
    type,
    x,
    y,
    width: size.width,
    height: size.height,
    rotation: 0,
    text: definition.label,
    style: { ...DEFAULT_NODE_STYLE },
    data,
    locked: false,
    hidden: false,
    groupId: null,
    entityFields,
    umlAttributes,
    umlMethods,
  };
}

export function createEdge(sourceId: string, targetId: string): DiagramEdge {
  return {
    id: crypto.randomUUID(),
    sourceId,
    targetId,
    label: '',
    style: 'orthogonal',
    stroke: '#667085',
    strokeWidth: 1.8,
    arrowEnd: true,
    arrowStart: false,
    sourceAnchor: 'auto',
    targetAnchor: 'auto',
    linePattern: 'solid',
    routingMode: 'auto',
    waypoints: [],
    sourceCardinality: undefined,
    targetCardinality: undefined,
    relationshipType: undefined,
    bundleId: null,
  };
}

export function createEmptyDocument(): DiagramDocument {
  const now = new Date().toISOString();
  const pageId = crypto.randomUUID();
  return {
    schemaVersion: 1,
    id: crypto.randomUUID(),
    title: 'Diagrama sem título',
    activePageId: pageId,
    pages: [{ id: pageId, name: 'Página 1', nodes: [], edges: [] }],
    createdAt: now,
    updatedAt: now,
    metadata: { app: 'OWDEX Draw', version: '1.0.0-rc3' },
  };
}

export function createWelcomeDocument(): DiagramDocument {
  const doc = createEmptyDocument();
  const page = doc.pages[0];
  const start = createNode('terminator', 140, 120);
  start.text = 'Começar';
  const process = createNode('rectangle', 390, 115);
  process.text = 'Crie seu processo';
  const decision = createNode('diamond', 660, 100);
  decision.text = 'Está correto?';

  page.nodes.push(start, process, decision);
  page.edges.push(createEdge(start.id, process.id), createEdge(process.id, decision.id));
  return doc;
}
